import React, { useState } from 'react';
import axios from 'axios';
import config from '../config';
import PatentCard from '../components/PatentCard';
import './Search.css';

function Search() {
  const [query, setQuery] = useState('');
  const [fields, setFields] = useState([
    'descriptive title', 'plain english summary', 'what the invention is'
  ]);
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [pagination, setPagination] = useState({ page: 1, total: 0, pages: 0 });
  const [hasSearched, setHasSearched] = useState(false);
  const [showAdvancedHelp, setShowAdvancedHelp] = useState(false);

  const advancedFieldRefs = [
    { key: 'inventor / inv', description: 'Inventor names from raw portfolio data' },
    { key: 'title', description: 'Descriptive title' },
    { key: 'summary', description: 'Plain English summary' },
    { key: 'technical / description', description: 'Technical description' },
    { key: 'problem', description: 'Problem space tags' },
    { key: 'solution', description: 'Solution space tags' },
    { key: 'domain', description: 'Domain tags' },
    { key: 'features', description: 'Key features list' },
    { key: 'cpc', description: 'CPC classification (JSON metadata)' },
    { key: 'ipc', description: 'IPC classification' },
    { key: 'abstract', description: 'Official abstract' },
    { key: 'claims', description: 'Official claims' }
  ];

  const fieldGroups = {
    'Core Information': [
      { value: 'descriptive title', label: 'Descriptive Title' },
      { value: 'plain english summary', label: 'Plain English Summary' },
      { value: 'technical description', label: 'Technical Description' },
      { value: 'what the invention is', label: 'What the Invention Is' },
      { value: 'what the invention does', label: 'What the Invention Does' }
    ],
    'Detailed Analysis': [
      { value: 'key features', label: 'Key Features' },
      { value: 'problem space', label: 'Problem Space' },
      { value: 'solution space', label: 'Solution Space' },
      { value: 'benefits', label: 'Benefits' },
      { value: 'how it works', label: 'How It Works' },
      { value: 'problem it solves', label: 'Problem It Solves' },
      { value: 'products and services', label: 'Products and Services' }
    ],
    'Raw Patent Data': [
      { value: 'official title', label: 'Official Title' },
      { value: 'official abstract', label: 'Official Abstract' },
      { value: 'official description', label: 'Official Description' },
      { value: 'official claims', label: 'Official Claims' }
    ]
  };

  const handleSearch = async (page = 1) => {
    if (!query.trim()) return;

    setLoading(true);
    setError(null);
    setHasSearched(true);

    try {
      const params = new URLSearchParams({
        q: query,
        page: page.toString(),
        per_page: '20'
      });
      
      fields.forEach(field => params.append('fields', field));

      const url = `${config.apiBaseUrl}/search?${params.toString()}`;
      console.log('Search URL:', url);
      const response = await axios.get(url);
      console.log('Search response:', response.data);
      setResults(response.data.results || []);
      setPagination({
        page: response.data.page || 1,
        total: response.data.total || 0,
        pages: response.data.pages || 0
      });
    } catch (err) {
      setError('Error searching patents. Please try again.');
      console.error('Search error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleFieldToggle = (field) => {
    setFields(prev => 
      prev.includes(field) 
        ? prev.filter(f => f !== field)
        : [...prev, field]
    );
  };

  const handleQueryChange = (e) => {
    setQuery(e.target.value);
    setHasSearched(false);
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  return (
    <div className="search-page">
      <h1>Search Patents</h1>

      <div className="search-container">
        <div className="search-input-group">
          <input
            type="text"
            placeholder="Keywords or field refs like inventor=smith (supports *, ?, AND, OR, NOT, parentheses)"
            value={query}
            onChange={handleQueryChange}
            onKeyPress={handleKeyPress}
            className="search-input"
          />
          <button
            type="button"
            className="search-help-btn"
            onClick={() => setShowAdvancedHelp(true)}
            aria-label="Advanced search help"
          >
            ?
          </button>
          <button onClick={() => handleSearch()} className="btn btn-primary">
            Search
          </button>
        </div>

        <div className="field-selector">
          <div className="field-header">
            <strong>Search in:</strong>
            {fields.length === 0 && (
              <span className="default-search-notice">
                No fields selected. Searching all Core and Detailed Analysis fields by default.
              </span>
            )}
          </div>
          <div className="field-groups">
            {Object.entries(fieldGroups).map(([groupName, groupFields]) => (
              <div key={groupName} className="field-group">
                <h3>{groupName}</h3>
                <div className="field-checkboxes">
                  {groupFields.map(field => (
                    <label key={field.value} className="field-checkbox">
                      <input
                        type="checkbox"
                        checked={fields.includes(field.value)}
                        onChange={() => handleFieldToggle(field.value)}
                      />
                      {field.label}
                    </label>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {loading && <div className="loading">Searching...</div>}

      {error && <div className="error">{error}</div>}

      {results.length > 0 && (
        <div className="search-results">
          <div className="results-header">
            <h2>Search Results</h2>
            <p>{pagination.total} patents found</p>
          </div>

          <div className="results-list">
            {results.map(patent => (
              <PatentCard key={patent.patent_id} patent={patent} />
            ))}
          </div>

          {pagination.pages > 1 && (
            <div className="pagination">
              <button
                onClick={() => handleSearch(pagination.page - 1)}
                disabled={pagination.page === 1}
              >
                Previous
              </button>
              <span>Page {pagination.page} of {pagination.pages}</span>
              <button
                onClick={() => handleSearch(pagination.page + 1)}
                disabled={pagination.page === pagination.pages}
              >
                Next
              </button>
            </div>
          )}
        </div>
      )}

      {!loading && hasSearched && results.length === 0 && (
        <div className="no-results">
          <p>No patents found matching "{query}"</p>
        </div>
      )}

      {showAdvancedHelp && (
        <div className="search-help-overlay" onClick={() => setShowAdvancedHelp(false)}>
          <div className="search-help-modal" onClick={(e) => e.stopPropagation()}>
            <div className="search-help-header">
              <h3>Advanced Search</h3>
              <button
                type="button"
                className="search-help-close"
                onClick={() => setShowAdvancedHelp(false)}
                aria-label="Close advanced search help"
              >
                ×
              </button>
            </div>
            <p className="search-help-intro">
              Use field references with = or contains, combine with AND / OR / NOT, and group expressions with parentheses.
            </p>
            <div className="search-help-fields">
              {advancedFieldRefs.map((field) => (
                <div key={field.key} className="search-help-field">
                  <code>{field.key}</code>
                  <span>{field.description}</span>
                </div>
              ))}
            </div>
            <div className="search-help-examples">
              <p>Examples</p>
              <code>inventor="smith" AND (cpc=H04 OR cpc="B64C")</code>
              <code>3D AND (inv="Smith" OR inv=jones) AND (problem=printing OR problem=additive OR problem=manufacture)</code>
              <code>solution contains "robotics" AND ipc=G05</code>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Search;
